export const meta = {
  id: '32-emotions',
  order: 32,
  titleFi: 'Tunteet',
  titleEn: 'Emotions',
  duolingoUnit: 'duo-38-emotions',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: [
    'ozmijegon', 'jaelagon', 'hobrenka', 'botagon', 'kirimves', 'usōvegon', 'raqnon',
    'buqagon', 'izūgagon', 'vēdros', 'iqighagon', 'jenigon', 'raqagon', 'sōpagon', 'munnon',
    'vaoresagon', 'jōzigon', 'mundagon', 'jaelarys', 'jorrāeliarza-2', 'imundagon',
    'ours:ondurarion',
    'ours:bāmvāzma',
    'ours:sōpnon',
    'ours:tārvos',
    'ours:ellanon', 'ours:tolmīhāves', 'ours:vēdrāzmenka', 'ours:hyrnon', 'ours:hakossiarzūljagon', 'ours:hunenkāves',
    'ours:jaelaroqitta',
    'ours:mundemagon',
    'ours:hyrikagon',
    'ours:uēmikagon',
    'ours:dēzemagon',
    'ours:raqikagon',
    'ours:nādrēje',
    'ours:ēdrȳntemagon',
    'ours:aerittemagon',
    'ours:nārijenka',
    'ours:nāqopsennon',
    'ours:ājenkāves',
    'ours:tistāves',
    'ours:tistēgrie',
    'ours:qrimpālella',
    'ours:nāijiōragon',
    'ours:iōrarys',
    'ours:symptomi',
    'ours:virusi',
    'ours:terapia',
    'ours:kanseri',
    'ours:konsientia',
    'ours:hylirion',
    'ours:urnȳnarys',
  ],
  exampleIds: [
    'ex-c899af670cbb',
    'ex-18efa0c1d100',
    'ex-7e165715356a',
    'hotd-s1e02-016',
    'ex-57866dfef974',
    'ex-7216f1eff677',
    'ex-4f7664db28e0',
    'ex-1790a2bdbb27',
    'ex-f09b8d868103',
    'hotd-s1e04-006',
    'ex-32beba3ab860',
    'ex-6ee57691711b',
    'ex-f84b2eaf6513',
    'hotd-s3e04-006',
    'hotd-s1e02-011',
    'hotd-s1e06-014',
    'ex-aea0e2f12385',
    'ex-c53f25e7a310',
    'ex-f94a05967da8',
    'ex-d68957bbdcf3',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Emotions',
    'https://wiki.languageinvention.com/index.php?title=syt',
    'https://wiki.languageinvention.com/index.php?title=ozmijegon',
    'https://wiki.languageinvention.com/index.php?title=jaelagon',
    'https://wiki.languageinvention.com/index.php?title=hobrenka',
    'https://wiki.languageinvention.com/index.php?title=usōvegon',
    'https://wiki.languageinvention.com/index.php?title=iqighagon',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Adpositions',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Moods#Uses_of_the_subjunctive',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Reference_Grammar#Noun_Declension',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['subjunctive'],
  showLines: ['hotd-s1e02-016', 'hotd-s1e04-006', 'hotd-s3e04-006', 'hotd-s1e02-011', 'hotd-s1e06-014'],
  pronunciation: { focus: 'palatals', words: ['qībōños', 'morghūljagon', 'ñōghybon', 'ñōghe', 'soljanna'] },
  letter: {
    fi: 'Kerro, miltä sinusta tuntuu tänään ja mitä kaipaat.',
    en: 'Write how you feel today and what you miss.',
  },
  scene: 'hotd-s3e04-1',
}

# Tunteet

Tunteista puhuminen on vieraalla kielellä vaikeinta, koska tunnesanat eivät käänny suoraan. Tällä oppitunnilla saat niitä parikymmentä ja niiden mukana neljä asiaa: postposition <Hv glossed>syt</Hv> ’jonkun hyväksi’, ikävöimistä tarkoittavan verbin, joka erottaa ihmiset ja esineet, subjunktiivin sen alkuperäisessä tehtävässä ja yhden sanan, jonka käännös ei kerro kaikkea.

## Syt: jonkun hyväksi

Postpositio <Hv glossed>syt</Hv> tarkoittaa ’varten, jonkun puolesta, jonkun hyväksi’: jotakin tehdään jollekulle tai jotakuta varten. Sitä edeltävä substantiivi on **genetiivissä**.

Tässä suomi auttaa enemmän kuin arvaisitkaan. Meidänkin postpositiomme vaativat genetiivin (*äidin puolesta*, *äidin hyväksi*), ja <Hv>syt</Hv> on sukua adjektiiville <Hv glossed>sȳz</Hv> ’hyvä’, aivan kuten suomen *hyväksi* on sukua sanalle *hyvä*.

<Compare glossed fi="äidi|n hyväksi" hv="muñ|o syt" label="genetiivi + postpositio" />

<Example id="ex-c899af670cbb" />

<Interlinear hv="Lōtint|i aō syt sēte|tan." gloss="piirakka-ACC sinä-GEN hyväksi tehdä-PRF.1SG" fi="Tein piirakan sinulle." />

<Example id="ex-18efa0c1d100" />

<Example id="ex-7e165715356a" />

<Line id="hotd-s1e02-016" />

Kysymys ’miksi?’ rakentuu samasta aineksesta: <Hv glossed>skoro syt</Hv>, suunnilleen ’mitä varten’.

<Example id="ex-57866dfef974" />

## Ikävä: ozmijegon

Uusi verbi <Hv glossed>ozmijegon</Hv> tarkoittaa ’kaivata, ikävöidä’ kuvaannollisessa mielessä. Sen objektin sija riippuu siitä, onko kohde **elollinen** vai eloton:

- jos kaipaat ihmistä (tai vaikkapa kilpikonnaa), kohde on **komitatiivissa**,
- jos kaipaat esinettä, esimerkiksi lempikirjaasi, kohde on **instrumentaalissa**.

Suomi ei tee tätä eroa: *kaipaan isää* ja *kaipaan hattua* ovat molemmat partitiivissa. Valyriassa ne eroavat:

<Compare fi="isä|ä" hv="kep|oma" label="partitiivi ~ komitatiivi (elollinen)" />

<Compare fi="hattu|a" hv="ziksann|osa" label="partitiivi ~ instrumentaali (eloton)" />

Ero ei kuitenkaan aina näy. Kertaa, missä <Term id="deklinaatio">taivutusluokissa</Term> instrumentaali ja komitatiivi ovat samat: esimerkiksi kolmannen <Term id="deklinaatio">taivutusluokan</Term> yksikössä (<Hv>vēdroso</Hv> on kumpaakin) ja toisen <Term id="deklinaatio">taivutusluokan</Term> yksikössä (<Hv>egromy</Hv>). Ensimmäisessä <Term id="deklinaatio">taivutusluokassa</Term> ne eroavat, kuten yllä: <Hv>keposa</Hv> ~ <Hv>kepoma</Hv>.

<Pronounce word="ozmijegon" />

<Example id="ex-7216f1eff677" />

<Interlinear hv="Val|a zȳh|om lyk|om kep|oma ozmije|s." gloss="mies-NOM oma-COM rauhallinen-COM isä-COM kaivata-PRS.3SG" fi="Mies kaipaa rauhallista isäänsä." />

<Example id="ex-4f7664db28e0" />

## Subjunktiivi palaa

Subjunktiivin muodot osaat jo kaikissa tähän mennessä opituissa aikamuodoissa ja pääluokissa, mutta olet käyttänyt niitä vain kieltolauseissa <Hv>daor</Hv>-sanan kanssa. Alun perin subjunktiivi oli kuitenkin **<Term id="sivulause">sivulauseiden</Term>** <Term id="tapaluokka">tapaluokka</Term>: sitä käytettiin, kun sivulauseen kuvaama teko oli epävarma, kuviteltu tai ehdollinen.

Tämän oppitunnin verbi <Hv glossed>jaelagon</Hv> ’toivoa, haluta’ vaatii sivulauseen verbin subjunktiiviin. Kolme esimerkkiä:

- <Hv glossed>Avy urnion jaelan.</Hv> ’Toivon, että näen sinut.’
- <Hv glossed>Taoba avy urnios jaelan.</Hv> ’Toivon, että poika näkee sinut.’
- <Hv glossed>Avy urnion taoba jaelza.</Hv> ’Poika toivoo, että minä näen sinut.’

<Interlinear hv="Taob|a avy urn|ios jael|an." gloss="poika-NOM sinä-ACC nähdä-PRS.SBJV.3SG toivoa-PRS.1SG" fi="Toivon, että poika näkee sinut." />

Huomaa kaksi asiaa, jotka tuntuvat suomalaisesta nurinkurisilta:

1. **Ei että-sanaa.** Valyriassa ei ole mitään, mikä vastaisi suomen *että*-sanaa.
2. **Päälause tulee perään.** Sivulause on ensin, ja päälause liitetään sen loppuun silloinkin, kun päälauseella on oma subjekti (<Hv fi="poika haluaa">taoba jaelza</Hv> kolmannessa esimerkissä).

Suomessa järjestys on päinvastainen: *Toivon, että…* Valyriaksi ajatus etenee kuin lukisit suomea lopusta alkuun: *sinut näkisin, toivon*. Suomen konditionaali voi auttaa tavoittamaan tunnelman, mutta se ei ole sama asia: suomessa saat valita (*toivon, että näen* tai *näkisin*), valyriassa subjunktiivi on tässä pakollinen.

Sivulause voi olla subjunktiivissa ja samalla kielteinen. Se näyttää täsmälleen samalta kuin kielteinen päälause:

- <Hv glossed>Taoba avy urnios daor jaelan.</Hv> ’Toivon, ettei poika näe sinua.’

<Paradigm lemma="urnegon" numbers="sg,pl" filter="subjunctive.active.present" />

<Example id="ex-1790a2bdbb27" />

Tässä toivotaan, että toinen kaipaa puhujaa: <Hv>ynoma</Hv> on komitatiivi (minä olen elollinen), <Hv>ozmijiō</Hv> subjunktiivi ja <Hv l="jaelagon">jaelan</Hv> päälause lopussa.

Sivulauseen subjunktiivi voi olla myös futuurissa (<Hv>ȳdrēlus</Hv>):

<Example id="ex-f09b8d868103" />

Daemon käyttää rakennetta lyhyimmillään. <Hv>Iksos</Hv> on olla-verbin subjunktiivi, ja <Hv glossed>jaelan</Hv> tulee taas viimeisenä: ’olisipa niin’.

<Line id="hotd-s1e04-006" />

Kun toivoja haluaa itse tehdä jotakin, <Hv l="jaelagon">jaelagon</Hv> voi saada myös infinitiivin, kuten Daenerysin lupauksessa:

<Example id="ex-32beba3ab860" />

<Pronounce l="jaelagon" word="jaelagon" />

## ?@#!%&

Adjektiivi <Hv glossed>hobrenka</Hv> käännetään ’typerä’, ja vain ’typerä’. Se ei ole kaunis sana, ja sen koko merkityskirjo jätetään mielikuvituksesi varaan. Sarjat nähneenä tiedät, ettei ’typerä’ ole koko totuus.

<Example id="ex-6ee57691711b" />

<Example id="ex-f84b2eaf6513" />

<Line id="hotd-s3e04-006" />

## Muut tunnesanat

Loput sanat kannattaa opetella pareittain. Moni niistä alkaa <Hv>i-</Hv>, <Hv>j-</Hv> tai <Hv>u-</Hv>-äänteellä, eikä se ole sattumaa. <LessonLink n={33}>Oppitunnilla 33</LessonLink> näet, että ne ovat tavallisten verbien johdoksia (<Hv glossed>izūgagon</Hv> ’pelätä’ ← <Hv>zūgagon</Hv>, <Hv glossed>imundagon</Hv> ’tuntea myötätuntoa’ ← <Hv glossed>mundagon</Hv> ’surra’, <Hv glossed>usōvegon</Hv> ’pyytää anteeksi’ ← <Hv>sōvegon</Hv>).

- <Hv glossed>raqagon</Hv> ’pitää, rakastaa’ ~ <Hv glossed>buqagon</Hv> ’vihata’
- <Hv glossed>kirine</Hv> ’iloinen, onnellinen’ (tuttu <LessonLink n={3}>oppitunnilta 3</LessonLink>), <Hv glossed>kirimves</Hv> ’ilo’ ~ <Hv glossed>mundagon</Hv> ’olla surullinen’, <Hv glossed>munnon</Hv> ’suru’
- <Hv glossed>raqnon</Hv> ’rakkaus’, <Hv glossed>jaelarys</Hv> ’toivo’, <Hv glossed>vēdros</Hv> ’viha’
- <Hv glossed>izūgagon</Hv> ’pelätä’, <Hv glossed>jōzigon</Hv> ’olla ylpeä jostakin’, <Hv glossed>vaoresagon</Hv> ’pitää enemmän, suosia’
- <Hv glossed>sōpagon</Hv> ’nauraa’, <Hv glossed>jenigon</Hv> ’koskettaa, vaivata, merkitä’, <Hv glossed>botagon</Hv> ’kärsiä, sallia’
- <Hv glossed>jorrāeliarza</Hv> ’rakas’

<Hv glossed>Iqighagon</Hv> ’saada joku tuntemaan jotakin’ ottaa ihmisen akkusatiivissa ja tunteen instrumentaalissa. Kun Rhaenyra sanoo ’olet suututtanut kuninkaasi’, kuningas (<Hv>dāri</Hv>) on akkusatiivissa ja viha (<Hv>vēdroso</Hv>) instrumentaalissa:

<Line id="hotd-s1e02-011" />

<Hv glossed>Usōvegon</Hv> ’pyytää anteeksi’ ottaa datiivissa sekä sen, jolta anteeksi pyydetään, että sen, mitä anteeksi pyydetään.

<Line id="hotd-s1e06-014" />

<Example id="ex-aea0e2f12385" />

<Example id="ex-c53f25e7a310" />

<Example id="ex-f94a05967da8" />

<Example id="ex-d68957bbdcf3" />

## Harjoituslauseita

<Practice hv="Muño syt bērio sētetan." fi="Tein tunikan äidille." />

<Practice hv="Kepoma ozmijen." fi="Kaipaan isää." />

<Practice hv="Ziksannosa ozmijen." fi="Kaipaan hattua." />

<Practice hv="Kepa avy urnios jaelan." fi="Toivon, että isä näkee sinut." />

<Practice hv="Kepa avy urnios daor jaelan." fi="Toivon, ettei isä näe sinua." />

## Puhutaan: vapaasti ja hahmossa

Tämän oppitunnin jälkeen avautuu puhumisen ylin askel. Puhukaa yhdestä aiheesta kolme kertaa: ensin neljä minuuttia, sitten kolme ja lopuksi kaksi. Aloittaja vaihtuu joka kierroksella. Kun sama asia sanotaan joka kerta lyhyemmässä ajassa, puhe alkaa sujua. Puhukaa vain valyriaa, mutta sopikaa etukäteen yksi suomen sana, jolla saa pyytää apua.

Lopuksi kumpikin napauttaa, toimiko keskustelu. Tämän oppitunnin tunnesanat sopivat yhteen ensimmäisistä aiheista, ”Miltä tänään tuntuu?”

<SpeakingStep step={5} />

## Muistettavaksi

- Genetiivi + <Hv glossed>syt</Hv> = jonkun hyväksi, jotakin varten.
- <Hv>Ozmijegon</Hv>: elollinen kohde komitatiivissa, eloton instrumentaalissa.
- <Hv l="jaelagon">Jaelagon</Hv> vaatii sivulauseen verbin subjunktiiviin. Ei *että*-sanaa; sivulause ensin, päälause loppuun.
- <Hv glossed>Hobrenka</Hv> on ’typerä’. Virallisesti.

<Vocab />

<Check />
